import { useEffect, useState } from "react";
import { fetchChartPoints, type ChartRange } from "@/api/datasets";
import type { ChartPoint, ChartSource } from "./chart-points";

/** The picker's choices; `custom` holds two dates. */
export type ChartRangeChoice = "all" | "7d" | "30d" | "90d" | "custom";

export function rangeChoice(range: ChartRange): ChartRangeChoice {
  return typeof range === "string" ? range : "custom";
}

function rangeKey(range: ChartRange): string {
  return typeof range === "string" ? range : `${range.from}..${range.to}`;
}

/** A local calendar day as "YYYY-MM-DD", `daysAgo` days back. */
function localDay(daysAgo: number): string {
  const day = new Date();
  day.setDate(day.getDate() - daysAgo);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** Custom dates the server can answer: both set, in order. */
function isComplete(range: ChartRange): boolean {
  return (
    typeof range === "string" ||
    (!!range.from && !!range.to && range.from <= range.to)
  );
}

interface Loaded {
  /** The request and revision these points answer. */
  key: string;
  points: ChartPoint[] | null;
  failed: boolean;
}

export interface ChartRangeState {
  range: ChartRange;
  pick: (choice: ChartRangeChoice) => void;
  setDates: (from: string, to: string) => void;
  /** The picked period's points; null while the stored points apply. */
  points: ChartPoint[] | null;
  /** True when a picked period's points could not be loaded. */
  failed: boolean;
}

/**
 * An opened chart's period (view state, never saved): it starts at the
 * source's range, whose points the chart stores. Another period asks the
 * server for its points, again when the table's `dataRevision` rises.
 */
export function useChartRange(
  projectId: string | null,
  source: ChartSource | null,
  dataRevision: unknown,
): ChartRangeState {
  const initial = source?.range ?? "all";
  const [picked, setPicked] = useState<ChartRange | null>(null);
  const range = picked ?? initial;
  const own =
    !!source && rangeKey(range) !== rangeKey(initial) && isComplete(range);
  const request =
    own && source && projectId
      ? { projectId, table: source.table, body: { ...source.request, range } }
      : null;
  const key = request
    ? `${JSON.stringify(request)}|${String(dataRevision)}`
    : "";
  const [loaded, setLoaded] = useState<Loaded>({
    key: "",
    points: null,
    failed: false,
  });

  useEffect(() => {
    if (!request) return;
    let current = true;
    fetchChartPoints(request.projectId, request.table, request.body).then(
      (result) => {
        if (current) setLoaded({ key, points: result.points, failed: false });
      },
      () => {
        if (current) setLoaded({ key, points: [], failed: true });
      },
    );
    return () => {
      current = false;
    };
    // `key` names the request and the revision it was made at.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    range,
    pick: (choice) => {
      if (choice !== "custom") {
        setPicked(choice);
        return;
      }
      setPicked(
        typeof initial === "string"
          ? { from: localDay(30), to: localDay(0) }
          : initial,
      );
    },
    setDates: (from, to) => setPicked({ from, to }),
    // While a period loads, the last picked period's points stay.
    points: request ? loaded.points : null,
    failed: !!request && loaded.key === key && loaded.failed,
  };
}
